import { useCallback, useState } from "react";
import { transferPointsService } from "../../services/api";

// The two prices, in one place. They were written into the dialog sentences of
// TenderCard and StaffTenderCard as bare numbers; the gate has to compare
// against the same figure the sentence names, so both now read it from here.
export const BOOST_COST = 400;
export const PIN_COST = 750;

// Boost and Pin are the only two paid actions on a club request, and until now
// their dialogs named the price without ever naming the balance: the club
// confirmed, and only THEN learned it could not afford it (HTTP 400), with the
// dialog left standing open. QAF01-027.
//
// The balance comes from GET /users/transfer-points/ via the existing
// transferPointsService.getBalance() — deliberately that one and not the
// figure in the profile payload, because for club roles this endpoint reads
// the very wallet the booking checks (club.transfer_points.balance). Reading a
// different field is exactly what produced the "two different balances" puzzle
// in QAF01-067, where the profile serializer leaked the wallet's row id.
//
// Fetched when a dialog OPENS, not per card: a requests page renders many
// cards and none of them needs the balance until someone opens Boost or Pin.
export const useTransferPointsCost = () => {
  const [balance, setBalance] = useState(null);
  const [loading, setLoading] = useState(false);

  const loadBalance = useCallback(async () => {
    setLoading(true);
    const res = await transferPointsService.getBalance();
    // A balance we could not read stays null, and null never disables the
    // button — a temporary API hiccup must not block an action the club can
    // actually afford. The backend remains the authority either way.
    setBalance(res.success && typeof res.data?.balance === "number" ? res.data.balance : null);
    setLoading(false);
  }, []);

  // The sentence under the dialog's headline, or null when there is nothing
  // trustworthy to say yet.
  const balanceLine = useCallback(
    (cost) => {
      if (loading) return "Checking your Transfer Points balance...";
      if (balance === null) return null;
      const short = cost - balance;
      return short > 0
        ? `Your balance: ${balance} Transfer Points — ${short} short for this action.`
        : `Your balance: ${balance} Transfer Points.`;
    },
    [balance, loading]
  );

  // Only a balance we actually read can block the button.
  const cannotAfford = useCallback(
    (cost) => balance !== null && balance < cost,
    [balance]
  );

  return { balance, loading, loadBalance, balanceLine, cannotAfford };
};

export default useTransferPointsCost;
